/**
 * Utility functions for calculating effective ownership percentages
 * through multi-level corporate structures.
 * 
 * Example: If Company X owns 50% of Company Y, and Individual Z owns 50% of Company X,
 * then Individual Z has 25% effective ownership in Company Y (50% × 50% = 25%)
 */

interface UBO {
  name: string;
  shareholding: number;
  type: 'individual' | 'company';
  ubos?: UBO[];
}

interface Shareholder {
  name: string;
  shareholding: number;
  type: 'individual' | 'company';
  ubos?: UBO[];
}

export interface EffectiveOwner {
  name: string;
  effectiveOwnership: number; // Percentage (0-100)
  path: string[]; // Chain of companies from root to this individual
  requiresBiometrics: boolean; // True if ≥25%
}

const BIOMETRICS_THRESHOLD = 25;

/**
 * Recursively calculates effective ownership for all individuals
 * in a corporate structure.
 */
function traverseOwnership(
  entity: Shareholder | UBO,
  parentOwnership: number,
  path: string[],
  results: Map<string, EffectiveOwner>
): void {
  const effectiveOwnership = (parentOwnership * entity.shareholding) / 100;
  
  if (entity.type === 'individual') {
    // Found an individual - add their effective ownership
    const existingOwner = results.get(entity.name);
    if (existingOwner) {
      // Same person may appear in multiple paths - accumulate ownership
      existingOwner.effectiveOwnership += effectiveOwnership;
      existingOwner.requiresBiometrics = existingOwner.effectiveOwnership >= BIOMETRICS_THRESHOLD;
    } else {
      results.set(entity.name, {
        name: entity.name,
        effectiveOwnership,
        path: [...path, entity.name],
        requiresBiometrics: effectiveOwnership >= BIOMETRICS_THRESHOLD
      });
    }
  } else if (entity.type === 'company' && entity.ubos && entity.ubos.length > 0) {
    // Company shareholder - traverse its UBOs
    const newPath = [...path, entity.name];
    for (const ubo of entity.ubos) {
      traverseOwnership(ubo, effectiveOwnership, newPath, results);
    }
  }
  // If company has no UBOs, we skip it (can't identify beneficial owners)
}

/**
 * Calculates effective ownership for all individuals in the shareholder structure.
 * Returns a list of all individuals with their effective ownership percentages.
 */
export function calculateEffectiveOwnership(shareholders: Shareholder[]): EffectiveOwner[] {
  const results = new Map<string, EffectiveOwner>();
  
  for (const shareholder of shareholders) {
    if (shareholder.type === 'individual') {
      // Direct individual shareholder
      const existingOwner = results.get(shareholder.name);
      if (existingOwner) {
        existingOwner.effectiveOwnership += shareholder.shareholding;
        existingOwner.requiresBiometrics = existingOwner.effectiveOwnership >= BIOMETRICS_THRESHOLD;
      } else {
        results.set(shareholder.name, {
          name: shareholder.name,
          effectiveOwnership: shareholder.shareholding,
          path: [shareholder.name],
          requiresBiometrics: shareholder.shareholding >= BIOMETRICS_THRESHOLD
        });
      }
    } else if (shareholder.type === 'company' && shareholder.ubos && shareholder.ubos.length > 0) {
      // Company shareholder - traverse its UBOs
      for (const ubo of shareholder.ubos) {
        traverseOwnership(ubo, shareholder.shareholding, [shareholder.name], results);
      }
    }
  }
  
  return Array.from(results.values()).sort((a, b) => b.effectiveOwnership - a.effectiveOwnership);
}

/**
 * Returns only individuals who require biometrics (≥25% effective ownership)
 */
export function getIndividualsRequiringBiometrics(shareholders: Shareholder[]): EffectiveOwner[] {
  return calculateEffectiveOwnership(shareholders).filter(owner => owner.requiresBiometrics);
}

/**
 * Formats the ownership path for display
 * Example: "ABC Ltd (50%) → XYZ Ltd (50%) → John Smith (100%)" = 25% effective
 */
export function formatOwnershipPath(owner: EffectiveOwner): string {
  return owner.path.join(' → ');
}
